import React, { FC, useCallback, useRef, useState } from 'react'
import homesteadStyles from '@/views/PreSales/PreSalesGiveawayView/styles/homestead-typography.module.css'
import classNames from 'classnames'
import { Button } from '@/atoms/Button'
import { FlexColumn } from '@/atoms/FlexContainers'
import { CaretDownIcon } from '@/atoms/Icons/CaretDownIcon'
import { PlayIcon } from '@/atoms/Icons/PlayIcon'
import { HomesteadLogo } from '@/atoms/Logos/HomesteadLogo'
import { Text } from '@/atoms/Text'
import { Translate, useTranslate } from '@/utils/translate/translate-client'
import GiveawayNav from '@/views/PreSales/PreSalesGiveawayView/components/GiveawayNav'
import { GiveawayVideoModal } from '@/views/PreSales/PreSalesGiveawayView/components/GiveawayVideoModal'
import { WeeklyCountdownTimer } from '@/views/PreSales/PreSalesGiveawayView/components/WeeklyCountdownTimer'

interface Props {
  onCtaClick: () => void
  onLearnMoreClick: () => void
  multiplier: number | null
  backgroundVideoSrc: string
  trailerUrl: string
  title: string
  heroRef: (node?: Element | null | undefined) => void
}
const GiveawayHero: FC<Props> = ({
  onCtaClick,
  multiplier,
  title,
  backgroundVideoSrc,
  trailerUrl,
  heroRef,
  onLearnMoreClick,
}) => {
  const [isVideoReady, setIsVideoReady] = useState(true)
  const [isTrailerModalOpen, setIsTrailerModalOpen] = useState(false)
  const { t } = useTranslate('tickets')
  const videoRef = useRef<HTMLVideoElement>(null)

  const handleOpenTrailer = useCallback(() => {
    videoRef?.current?.pause()
    setIsTrailerModalOpen(true)
  }, [])

  const handleCloseTrailer = useCallback(() => {
    videoRef?.current?.play().catch()
    setIsTrailerModalOpen(false)
  }, [])

  return (
    <>
      <header
        ref={heroRef}
        className="relative flex h-dvh flex-col bg-black bg-[linear-gradient(305deg,rgba(0,0,0,0.25)_10%,rgba(0,0,0,0.45)_100%),url(https://images.angelstudios.com/image/upload/v1730306319/homestead-key-art-main-tall-02.webp)] bg-cover bg-no-repeat p-6 md:max-h-[856px] md:bg-[linear-gradient(305deg,rgba(0,0,0,0.25)_10%,rgba(0,0,0,0.45)_100%),url(https://images.angelstudios.com/image/upload/v1730306263/homestead-key-art-main-wide-01.webp)] md:bg-center"
      >
        <div
          className={`absolute inset-0 h-full transition-opacity duration-1000 ${
            isVideoReady ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <video
            key={backgroundVideoSrc}
            ref={videoRef}
            id={backgroundVideoSrc}
            className="!h-full w-full overflow-hidden object-cover"
            autoPlay
            muted
            loop
            playsInline
            onCanPlay={() => setIsVideoReady(true)}
          >
            <source src={backgroundVideoSrc} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        </div>

        <GiveawayNav />

        <div className="relative mx-auto mt-4 flex h-full w-full max-w-[1080px] flex-col gap-4 md:flex-row md:items-center md:justify-center md:gap-10">
          <FlexColumn className="gap-4 md:shrink-0">
            <HomesteadLogo className="hidden md:block" width={265} />

            <InTheatersPill className="mt-4 hidden md:flex" />

            <h1 className="md:!homestead-display text-center !font-[dharma-gothic-e] text-[2.5rem] font-extrabold uppercase leading-[38px] tracking-[1.5px] text-white md:max-w-[540px] md:text-start">
              {title}
            </h1>

            <div className="-mx-6 bg-homestead-yellow py-2.5 text-center md:mx-0 md:w-fit md:px-6 md:py-4 md:text-left">
              <p className="!homestead-eyebrow-lg !font-[bio-sans] text-sm font-semibold uppercase leading-[14px] tracking-[0.5px] text-homestead-primary-blue">
                {t('thisWeekOnly', 'This week only')}
              </p>
              <p className="!font-[bio-sans] text-xl font-semibold leading-[26px] text-homestead-primary-blue md:text-[2rem] md:leading-[41.6px]">
                <Translate t={t} i18nKey="getMultiplierYourEntires">
                  Get <span className="!font-extrabold">{{ multiplier }}x</span> Your Entries
                </Translate>
              </p>

              <div className="flex justify-center gap-2 md:justify-start">
                <p className="!homestead-eyebrow-lg uppercase text-homestead-primary-blue md:text-lg md:leading-[23.4px]">
                  {t('endingIn', 'Ending In')}
                </p>
                <WeeklyCountdownTimer className="!homestead-eyebrow-lg !ml-0 text-homestead-primary-blue md:text-lg md:leading-[23.4px]" />
              </div>
            </div>

            <Button
              className="hidden flex-col rounded-[14px] border-none !bg-homestead-red !py-[17px] md:mr-auto md:mt-4 md:flex md:!px-7"
              onClick={onCtaClick}
              variant="core-gray-900"
            >
              <Text color="white" className="homestead-btn-md uppercase">
                {t('preOrderTicketsGiveaway', 'Pre-Order Tickets')}
              </Text>
            </Button>
          </FlexColumn>

          <div className="m-auto flex items-center justify-center md:w-full">
            <button
              className="flex min-w-fit basis-1 cursor-pointer flex-col items-center justify-center gap-2"
              onClick={handleOpenTrailer}
            >
              <div className="cursor-pointer rounded-[50px] bg-white px-8 py-4 backdrop-blur">
                <PlayIcon size={40} className="md:!h-[48px] md:!w-[48px]" color="homestead-primary-blue" />
              </div>
              <Text className={classNames(homesteadStyles.EyebrowMD, 'text-white md:!text-base uppercase')}>
                <span style={{ textShadow: '1px 1px 0px 0px rgba(0,0,0, 0.35)' }}>
                  {t('watchGiveawayVideo', 'Watch Giveaway Video')}
                </span>
              </Text>
            </button>
          </div>

          <FlexColumn className="gap-6 md:hidden">
            <FlexColumn className="items-center gap-3 md:hidden">
              <HomesteadLogo width={265} />
              <InTheatersPill />
            </FlexColumn>

            <Button
              className="flex flex-col rounded-[14px] border-none !bg-homestead-red !py-[17px] md:mr-auto md:!px-7"
              onClick={onCtaClick}
              variant="core-gray-900"
            >
              <Text color="white" className="homestead-btn-md uppercase">
                {t('preOrderTicketsGiveaway', 'Pre-Order Tickets')}
              </Text>
            </Button>
          </FlexColumn>
        </div>

        <button
          className="absolute bottom-8 left-1/2 hidden -translate-x-1/2 cursor-pointer flex-col justify-center md:flex"
          onClick={onLearnMoreClick}
        >
          <p color="white" className="!homestead-eyebrow-md cursor-pointer text-white">
            <span style={{ textShadow: '1px 1px 0px rgba(0,0,0, 0.5)' }}>{t('learnMore', 'Learn More')}</span>
          </p>
          <div className="mx-auto mt-2 w-fit rounded-full bg-white/20 p-[10px] backdrop-blur">
            <CaretDownIcon size={20} color="white" />
          </div>
        </button>
      </header>

      <GiveawayVideoModal handleClose={handleCloseTrailer} showModal={isTrailerModalOpen} youTubeUrl={trailerUrl} />
    </>
  )
}

export default GiveawayHero

const InTheatersPill = ({ className }: { className?: string }) => {
  const { t } = useTranslate('tickets')

  return (
    <p
      className={classNames(
        '!homestead-eyebrow-md w-fit items-center justify-center rounded-md px-2 py-1 font-semibold uppercase md:inline-flex bg-[#535F66] text-white',
        className,
      )}
    >
      {t('inTheatersDec20', 'In theaters dec. 20th')}
    </p>
  )
}
